
import { Vue, Component, PropSync, Prop } from "vue-property-decorator";
import FormModal from "@/components/base/FormModal.vue";
import BaseAutocomplete from "@/components/base/design/BaseAutocomplete.vue";
import BaseSelect from "@/components/base/design/BaseSelect.vue";
import WeekPicker from "@/components/base/pickers/WeekPicker.vue";
import ImageInput from "@/components/base/form/ImageInput.vue";
import EditModeTextField from "@/components/base/form/EditModeTextField.vue";
import { IBaseAutocomplete } from "@/types";
import { ICountry, ICurrency } from "@/api/types/catalogue";
import {
    addOfferRoundLogo,
    getEmployeesOffersV2,
    getOneEmployeesOffer,
    postOffersTopOfWeek,
} from "@/api/offers";
import { getCurrencyIcon } from "@/utils/translate";
import SvgTrash from "@/assets/icons/trash.svg";
import SvgTrashDark from "@/assets/icons/trash-dark.svg";
import SvgPlus from "@/assets/icons/plus.svg";
import SvgCreate from "@/assets/icons/magic-wand-light.svg";
import { namespace } from "vuex-class";
import { showNotification, showServerError } from "@/utils";
import { eventBus } from "@/eventbus";
import { ICreateOfferTopOfWeek, IOfferTopOfWeek, IProduct } from "@/api/types/offers";
import BaseTooltip from "@/components-UI/BaseTooltip.vue";
import BaseButton from "@/components-UI/BaseButton.vue";
import SvgReload from "@/assets/icons/reload.svg";
import SvgDrag from "@/assets/icons/drag-icon.svg";
import draggable from 'vuedraggable';

enum STATUS {
    DRAFT = 'draft',
    PUBLISHED = 'published',
}

interface ITopOffer {
    id?: number;
    localId: number;
    epc: number;
    logo: null | string | File;
    name: string;
}

const catalogue = namespace('catalogueModule');

@Component({
    methods: { getEmployeesOffersV2, getCurrencyIcon },
    components: {
        SvgDrag,
        SvgReload,
        BaseButton, BaseTooltip,
        SvgPlus,
        SvgCreate,
        SvgTrashDark,
        EditModeTextField,
        ImageInput,
        BaseAutocomplete,
        BaseSelect,
        WeekPicker,
        FormModal,
        SvgTrash,
        draggable,
    },
})

export default class CreateTopOfWeek extends Vue {
    @PropSync('showModal', { required: true }) showModalSync!: boolean;
    @Prop({ type: Array, required: true }) productList!: IProduct[];

    @catalogue.Getter('GET_COUNTRIES') countries!: ICountry[];
    @catalogue.Getter('GET_CURRENCIES') currencies!: ICurrency[];

    STATUS = STATUS;

    countrySelectOptions: IBaseAutocomplete = {
        itemValue: 'code',
        itemText: 'name',
        template: (i: any): any => i.name,
        clearable: true,
    };
    header = [
        { name: 'Страна', type: 'country' },
        { name: 'Тип',    type: 'products' },
        { name: 'Неделя', type: 'week' },
        { name: 'Валюта', type: 'currency' },
    ];
    product: string = '';
    productWrong: string = '';
    country: string = '';
    currency: string = '';
    period: string[] = [];
    newOffer = {} as ITopOffer;
    offers: ITopOffer[] = [];
    status: string = STATUS.DRAFT;
    startId: number = 0;
    isFullHeight: boolean = false;

    mounted(): void {
        this.addOfferTemplate();
    }

    get getCountries(): ICountry[] {
        const ru = this.countries.find(i => i.code === 'RU');
        const all = this.countries.filter(i => i.code !== 'RU');
        return [ ru!, ...all ];
    }
    get getCurrencies(): ICurrency[] {
        const rub = this.currencies.find(i => i.code === 'RUB');
        const all = this.currencies.filter(i => i.code !== 'RUB');
        return [ rub!, ...all ];
    }

    get isMobile(): boolean {
        return this.$vuetify.breakpoint.width <= 980;
    }

    get isFullHead(): boolean {
        const productParam = this.country === 'RU' ? this.product !== '' : this.productWrong !== '';
        return !!this.currency && !!this.country && this.period.length > 0 && productParam;
    }

    get isBlockPublish(): boolean {
        const isBlock = !this.offers.every(i => (typeof i.logo === 'string' && i.logo.length > 0) || i.logo instanceof File);
        if (isBlock) this.status = STATUS.DRAFT;
        return isBlock;
    }

    get isDisableAdd(): boolean {
        return this.offers.length > 9 || !this.isFullHead;
    }

    get isDisableCreate(): boolean {
        return !(this.offers.every(i => !!i.id && !!i.epc) && this.isFullHead);
    }

    get getLocalId(): number {
        //Для адекватного поведения списка
        const id = this.offers.length === 0 ? 1 : this.offers[this.offers.length - 1].localId + 1;
        return id + this.startId;
    }

    get getClasses(): string {
        return `create-top ${this.isFullHeight ? '--full-height' : ''}`;
    }

    closeTop(): void {
        this.showModalSync = false;
        this.isFullHeight = false;
        this.clearTop();
    }

    clearTop(): void {
        this.period = [];
        this.country = '';
        this.currency = '';
        this.status = STATUS.DRAFT;
        this.offers = [];
        eventBus.$emit('clear');
        this.addOfferTemplate();
    }

    checkHeight(): void {
        const card = document.querySelector('.form-modal__v-card');
        if (!card) return;

        const cardTitle = card.querySelector('.v-card__title');
        const cardText = card.querySelector('.v-card__text');
        const cardActions = card.querySelector('.v-card__actions');
        if(!cardTitle || !cardText || !cardActions ) return;

        const summElements = cardTitle.clientHeight + cardText.clientHeight + cardActions.clientHeight;
        this.isFullHeight = summElements > window.innerHeight * 0.85;
    }

    addOfferTemplate(): void {
        this.newOffer = { id: undefined, localId: this.getLocalId, epc: 0, logo: null, name: '' };
        this.offers.push(this.newOffer);
        this.checkHeight();
    }

    addOffer(): void {
        this.newOffer = {} as ITopOffer;
        this.addOfferTemplate();
    }

    changeCountry( data: string ): void {
        this.country = data;
        this.productWrong = data.length ? 'Общий' : '';
    }

    async chooseOffer(id: number, offer: ITopOffer): Promise<void> {
        if (!id) return;
        try {
            const { logo_round, name } = await getOneEmployeesOffer(id, { by_legacy_id: 1 });
            offer.id = id;
            offer.logo = logo_round;
            offer.name = name;
        } catch (err) {
            showServerError(err, 'Оффер не загружен');
        }
    }

    deleteOffer(item: any): void {
        if (this.offers.length === 1) {
            this.offers = [];
            this.startId++;
            this.addOfferTemplate();
            return;
        }
        if (!item.id || !item.epc) {
            this.newOffer = {} as ITopOffer;
        }
        const index = this.offers.findIndex((i) => i.id === item.id);
        this.offers.splice(index, 1);
        this.checkHeight();
    }

    async createTop(): Promise<void> {
        try {
            await this.uploadLogo();
            const offers = this.offers.map(({ id, epc }) => ({ id, epc })) as IOfferTopOfWeek[];
            const period = this.period[0];
            const top: ICreateOfferTopOfWeek = {
                period,
                offers,
                country: this.country,
                currency: this.currency,
                status: this.status,
            };
            if( this.productWrong !== ''){
                if( this.product !== '' ){
                    const foundProduct = this.productList.find( (productItem:IProduct) => productItem.name! === this.product );
                    top.product_id = foundProduct!.id;
                }else{
                    top.product_id = null;
                }
            }
            await postOffersTopOfWeek(top);
            showNotification('Топ успешно создан');
            this.showModalSync = false;
            this.$emit('update');
        } catch (err) {
            showServerError(err, 'Топ не создан');
        }
    }

    async uploadLogo(): Promise<void> {
        try {
            const offersWithNewLogo = this.offers.filter(i => i.logo instanceof File);
            await Promise.all(offersWithNewLogo.map(({ id, logo }) => addOfferRoundLogo(logo as File, id as number)));
        } catch (err) {
            showServerError(err, 'Ошибка загрузки логотипа');
        }
    }
}
